import type { SetValue } from './scoring-match-draft';
import type { ScoringSystem } from './scoring-types';

// Mirror of `awardSetPoints`/`awardMatchPoints` in @tournament-suite/league-rules —
// the viewer is a standalone CRA app and cannot import workspace packages. The
// group's own ScoringSystem drives it, so a league that retunes the point values
// stays in step with the "Pkt" column the API computes. Keep both in sync.
export interface AwardedPoints {
  side1: number;
  side2: number;
}

function awardTo(winnerSide: 1 | 2, winnerPoints: number, loserPoints: number): AwardedPoints {
  if (winnerSide === 1) {
    return { side1: winnerPoints, side2: loserPoints };
  }

  return { side1: loserPoints, side2: winnerPoints };
}

export function awardSetPoints(scoring: ScoringSystem, p1: number, p2: number): AwardedPoints {
  if (p1 === p2) {
    return { side1: scoring.drawPoints, side2: scoring.drawPoints };
  }

  const winnerSide = p1 > p2 ? 1 : 2;
  const isDominant =
    scoring.dominantMargin !== null && Math.abs(p1 - p2) >= scoring.dominantMargin;

  if (!isDominant) {
    return awardTo(winnerSide, scoring.winPoints, scoring.lossPoints);
  }

  return awardTo(
    winnerSide,
    scoring.dominantWinPoints ?? scoring.winPoints,
    scoring.dominantLossPoints ?? scoring.lossPoints,
  );
}

// Null means "no points to show": either the group does not award per-set points,
// or nothing is filled in yet. A partially entered match still shows the points of
// the sets already typed, so the organiser sees them build up while entering.
export function matchPoints(
  scoring: ScoringSystem | undefined,
  sets: SetValue[],
): AwardedPoints | null {
  if (scoring?.perSet !== true) {
    return null;
  }

  const scored = sets.filter((set) => set.p1 !== '' && set.p2 !== '');
  if (scored.length === 0) {
    return null;
  }

  return scored.reduce<AwardedPoints>(
    (total, set) => {
      const awarded = awardSetPoints(scoring, Number(set.p1), Number(set.p2));

      return { side1: total.side1 + awarded.side1, side2: total.side2 + awarded.side2 };
    },
    { side1: 0, side2: 0 },
  );
}
